import { useMemo, useState } from "react";
import { useTable } from "react-table";
import { defaultDateDisplay, formatCurrency } from "../../../helpers";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { pathConstants } from "../../../routes/pathContants";
import useStoreFrontDisplay from "../../../hooks/useStoreFrontDisplay";
import { useQuery } from "react-query";
import { getOrderTables } from "../../../services/inventory";
import useChowdeckTableDisplay from "../../../hooks/useChowdeckTableDisplay";
import globalStyles from "../../../assets/styles/base/globalPage.module.scss";

const useSalesTransactionsTable = ({
  salesTransactions,
  showpricing,
  selectedTab,
  isViaSellMode = false,
  handleRefetchOrders = () => {},
}) => {
  const { store_id } = useParams();
  const [search] = useSearchParams();
  const [selectedOrder, setSelectedOrder] = useState(
    search.get("orderId") || null,
  );
  const { columns: storefrontColumns } = useStoreFrontDisplay({
    setSelectedOrder,
    handleRefetchOrders,
  });

  const { columns: chowdeckColumns } = useChowdeckTableDisplay({
    setSelectedOrder,
    handleRefetchOrders,
  });

  const { data: orderTables } = useQuery(
    ["getOrderTables", store_id],
    () => getOrderTables("", { store_id }).then((res) => res.data),
    { staleTime: Infinity },
  );

  const columns = useMemo(
    () => [
      {
        Header: "Order No & Date",
        accessor: "id",
        Cell: ({ cell: { value }, row }) => (
          <span>
            <span
              onClick={() => {
                setSelectedOrder(value);
              }}
              className="purple--text cursor-pointer"
              style={{
                textDecoration: "underline",
              }}
            >
              {value}
            </span>
            <span className="d-block">
              {row?.original?.order_dates?.length > 0
                ? defaultDateDisplay(
                    row?.original?.order_dates?.[
                      row?.original?.order_dates?.length - 1
                    ],
                  )
                : defaultDateDisplay(row?.original?.created_at)}
            </span>
          </span>
        ),
      },
      {
        Header: "Payment Type",
        accessor: "payment_method",
        Cell: ({ cell: { value } }) => (
          <span>{value === null ? "N/A" : value?.replace("_", " ")}</span>
        ),
      },
      {
        Header: "Amount",
        accessor: "total_amount",
        Cell: ({ cell }) => (
          <span>
            {showpricing
              ? formatCurrency({
                  value: cell.value,
                  currencyCode: cell?.row?.original?.store?.currency_code,
                })
              : "*****"}
          </span>
        ),
      },
      {
        Header: "Return Amount",
        accessor: "total_returned_amount",
        Cell: ({ cell: { value }, row }) => (
          <span
            style={{
              color: value < 0 ? "#E02020" : "#27AE60",
              fontWeight: 500,
            }}
          >
            {formatCurrency({
              value: value,
              currencyCode: row?.original?.store?.currency_code,
            })}
          </span>
        ),
      },
      {
        Header: "Return Status",
        accessor: "return_status",
        Cell: ({ cell: { value } }) => {
          let backgroundColor = "#EFEFEF";
          let textColor = "#666";
          let displayText = "NO RETURN";

          if (value === "FULL_RETURN") {
            backgroundColor = "#FFEEE9";
            textColor = "#E02020";
            displayText = "FULL RETURN";
          } else if (value === "PARTIAL_RETURN") {
            backgroundColor = "#FEF7EB";
            textColor = "#F2994A";
            displayText = "PARTIAL RETURN";
          }

          return (
            <span
              className={`${globalStyles.statusBlock} gray--text`}
              style={{
                background: backgroundColor,
                color: textColor,
                whiteSpace: "nowrap",
                fontSize: "10px",
              }}
            >
              {displayText}
            </span>
          );
        },
      },
      {
        Header: "Payment Reconciled ",
        Cell: ({ row }) => (row?.original?.trx_reference ? "Yes" : "No"),
      },

      {
        Header: "Customer",
        accessor: "customer",
        Cell: ({ cell: { value }, row }) =>
          value && !isViaSellMode ? (
            <Link
              className="smallest-text-size purple--text"
              to={pathConstants.CUSTOMER_DETAILS({
                customerId: row?.original?.customer?.id,
              })}
              style={{ textDecoration: "underline" }}
            >
              {value?.full_name}
            </Link>
          ) : (
            value?.full_name || "N/A"
          ),
      },
      ...(orderTables?.data?.length > 0
        ? [
            {
              Header: "Table",
              accessor: "table",
              Cell: ({ cell: { value } }) => (
                <span>{value?.name || "N/A"}</span>
              ),
            },
          ]
        : []),
      {
        Header: "Cashier",
        accessor: "cashier",
        Cell: ({ cell: { value } }) => (
          <span>
            {value === null || value === undefined || value?.first_name === ""
              ? "N/A"
              : value?.first_name}
          </span>
        ),
      },
      {
        Header: "Store",
        accessor: "store",
        Cell: ({ cell: { value } }) => <span>{value?.name || "N/A"}</span>,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ cell: { value } }) => (
          <span
            className={globalStyles.statusBlock}
            style={{
              ...handleRenderStatusIndicator(value),
            }}
          >
            {value?.replace("_", " ")}
          </span>
        ),
      },
      {
        Header: "Order Entry Date",
        accessor: "created_at",
        Cell: ({ cell: { value } }) => <span>{defaultDateDisplay(value)}</span>,
      },
    ],
    [selectedTab, orderTables],
  );

  const handleRenderStatusIndicator = (value) => {
    if (value === "COMPLETED" || value === "ORDER_READY") {
      return { color: "#19AE57", backgroundColor: "#E5FFF0" };
    } else if (value === "IN_PROGRESS") {
      return { color: "#F7AD3C", backgroundColor: "#FEF7EB" };
    } else if (value === "PLACED") {
      return { color: "#F7AD3C", backgroundColor: "#FEF7EB" };
    } else {
      return { color: "#E02020", backgroundColor: "#FFEEE9" };
    }
  };

  const instance = useTable({
    columns:
      search.get("order_source") === "STOREFRONT"
        ? storefrontColumns
        : search.get("order_source") === "CHOWDECK" ||
            search.get("order_source") === "SQUAREUP"
          ? chowdeckColumns
          : columns,
    data: salesTransactions,
  });

  return {
    instance,
    selectedOrder,
    setSelectedOrder,
  };
};

export default useSalesTransactionsTable;
